
import React from 'react';
import { Play, Pause } from 'lucide-react';
import { Prayer } from '@/types/prayer';
import { useAbielAudioPlayer } from '@/hooks/useAbielAudioPlayer';
import { useAbielAudio } from '@/components/audio/AbielAudioContext';

interface AudioControlButtonProps {
  prayer: Prayer;
  baseButton: string;
  accentGlow: string;
}

export default function AudioControlButton({
  prayer,
  baseButton,
  accentGlow
}: AudioControlButtonProps) {
  const { togglePrayer, isPlayingPrayer, isLoading } = useAbielAudioPlayer();
  const { revealControls, controlsVisible, isPlaying, pause } = useAbielAudio();

  const handleTogglePlay = async () => {
    console.info('[AudioControlButton] Toggle play clicked for prayer:', prayer.id, 'controlsVisible:', controlsVisible, 'isPlaying:', isPlaying);
    
    // If controls are hidden (floating mode), reveal them first
    if (!controlsVisible) {
      console.info('[AudioControlButton] Revealing controls in floating mode');
      revealControls();
    }
    
    // Smart pause: if any audio is playing, pause it directly (handles session ID mismatches)
    if (isPlaying) {
      console.info('[AudioControlButton] Audio is playing, pausing directly');
      pause();
      return;
    }
    
    // No audio playing, start this prayer
    try {
      console.info('[AudioControlButton] No audio playing, starting prayer:', prayer.id);
      await togglePrayer(prayer);
    } catch (error) {
      console.error('[AudioControlButton] Failed to toggle audio playback:', error);
    }
  };

  // Use the centralized state checking from the hook
  const isPrayerPlaying = isPlayingPrayer(prayer.id);
  const canPlay = Boolean(prayer.content && prayer.content.length > 10);

  console.debug('[AudioControlButton] Enhanced button render state:', { 
    prayerId: prayer.id, 
    isPrayerPlaying, 
    canPlay,
    isLoading: isLoading,
    buttonVisible: true
  });

  return (
    <button
      onClick={handleTogglePlay}
      disabled={!canPlay || isLoading}
      className={`${baseButton} ${accentGlow} group transition-all duration-300 ease-out transform active:scale-95
        ${isPrayerPlaying && !isLoading ? 'scale-110 shadow-lg ring-gold/30' : isLoading ? 'animate-pulse' : 'hover:scale-105'}`}
      aria-label={isLoading ? "Generating voice..." : isPrayerPlaying ? "Pause voice" : "Listen to prayer"}
      title={isLoading ? "Generating voice audio..." : "Audio Control"}
    >
      <div className={`transition-all duration-200 ${isPrayerPlaying && !isLoading ? 'animate-pulse' : ''}`}>
        {isLoading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gold border-t-transparent" title="Generating TTS audio..." />
        ) : isPrayerPlaying ? (
          <Pause size={16} className="text-black dark:text-gold transition-all duration-200" />
        ) : (
          <Play size={16} className="text-black dark:text-gold ml-0.5 transition-all duration-200" />
        )}
      </div>
    </button>
  );
}
